import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useState } from 'react';
import { DataTableNew } from '../../../design-system/data-table-new';
import { BeneficiaryView } from '../../../types/entity-types';
import { useDeleteBeneficiary } from '../hooks/use-delete-beneficiary';
import { beneficiaryTableColumnsDefinition } from './beneficiary-table-column-definition';
import { DeleteBeneficiaryModal } from './components/delete-beneficiary-modal';

type LastPaidBeneficiariesTableProps = {
  beneficiaries: BeneficiaryView[];
  onInitiateBeneficiaryPayment: (beneficiaryId: string) => void;
};

export const LastPaidBeneficiariesTable = ({
  beneficiaries,
  onInitiateBeneficiaryPayment,
}: LastPaidBeneficiariesTableProps) => {
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    beneficiaryId: string | null;
    beneficiaryName: string;
    lastPaidDate?: Date;
  }>({
    isOpen: false,
    beneficiaryId: null,
    beneficiaryName: '',
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const { deleteBeneficiary } = useDeleteBeneficiary();

  const handleDeleteBeneficiary = (beneficiaryId: string, beneficiaryName: string, lastPaidDate?: Date) => {
    setDeleteModalState({
      isOpen: true,
      beneficiaryId,
      beneficiaryName,
      lastPaidDate,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModalState.beneficiaryId) return;

    setIsDeleting(true);
    try {
      await deleteBeneficiary(deleteModalState.beneficiaryId);
      setDeleteModalState({ isOpen: false, beneficiaryId: null, beneficiaryName: '' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseModal = () => {
    if (!isDeleting) {
      setDeleteModalState({ isOpen: false, beneficiaryId: null, beneficiaryName: '' });
    }
  };

  const table = useReactTable({
    data: beneficiaries,
    columns: beneficiaryTableColumnsDefinition,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    meta: {
      onInitiateBeneficiaryPayment,
      onDeleteBeneficiary: handleDeleteBeneficiary,
    },
    initialState: {
      sorting: [{ id: 'lastPaid', desc: true }],
      pagination: {
        pageSize: 5,
      },
    },
  });

  return (
    <>
      <DataTableNew.Table table={table} noResultsMessage="No recent recipients" />
      <DeleteBeneficiaryModal
        isOpen={deleteModalState.isOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        beneficiaryName={deleteModalState.beneficiaryName}
        lastPaidDate={deleteModalState.lastPaidDate}
        isLoading={isDeleting}
      />
    </>
  );
};
